import { StateMapping, UiState } from 'instantsearch.js';
import slugify from 'slugify';
import { isSortTransitionInProgress } from '@/helpers/utils/dealsFacetUtils';
interface CustomRouteState {
  q?: string;
  facet?: string;
  sort?: string;
  page?: string;
  styleFinder?: string;
  [key: string]: any;
}
export const defaultFacets: any = {
  gender: 1,
  colorRefinement: 2,
  brandName: 3,
  refinementStyle: 4,
  refinementSubDept: 5,
};

export const slugifyFacet = (facet: string) => {
  return slugify(facet, {
    replacement: '-',
    remove: undefined,
    lower: true,
    strict: false,
    trim: true,
  });
};

const CustomStateMapping = (indexName: string, indexableFacets?: any): StateMapping<UiState, CustomRouteState> => ({
  stateToRoute(uiState) {
    let facetsToMap = defaultFacets;
    try {
      facetsToMap = indexableFacets ? JSON.parse(indexableFacets) : defaultFacets;
    } catch (error) {}
    const indexUiState = uiState[indexName] || {};
    const url = new URL(window.location.href);
    const baseURl = url.pathname.split('/_/')[0];
    if (url.pathname != baseURl) {
      url.pathname = baseURl;
    }

    const facet: string[] = [];
    const facetsToUrl: any = {};
    const sfacet: string[] = [];

    // Build the facet string based on refinements in state
    if (indexUiState.refinementList) {
      for (const [attribute, values] of Object.entries(indexUiState.refinementList)) {
        if (values.length > 0) {
          facet.push(`${attribute}:${values.join(',')}`);
        }
        if (facetsToMap[attribute] && values.length == 1) {
          facetsToUrl[attribute] = values[0];
        }
      }
    }
    const pageLoadFacets = typeof window !== 'undefined' ? sessionStorage.getItem('pageLoadFacets') : '';
    if (
      pageLoadFacets !== '' &&
      pageLoadFacets &&
      (url.pathname.includes('categories') || url.pathname.includes('brands') || url.pathname.includes('search'))
    ) {
      const pageLoadFacetsList = pageLoadFacets?.split('|');

      type RefinementList = { [key: string]: string[] };
      const refinementList: RefinementList = {};

      pageLoadFacetsList.forEach((facet) => {
        const [attribute, ...valueParts] = facet.split(':');
        const value = valueParts.join(':'); // Rejoin the remaining parts
        if (attribute && value) {
          refinementList[attribute] = value.split(',');
        }
      });

      const validRefinementList: RefinementList = indexUiState.refinementList || {};
      Object.keys(refinementList).forEach((attribute) => {
        const requestValues = refinementList[attribute];
        const validValues = validRefinementList[attribute] || [];
        const difference = requestValues.filter((value) => !validValues.includes(value));

        if (difference.length > 0) {
          sfacet.push(`${attribute}:${refinementList[attribute]}`);
          facet.push(`${attribute}:${refinementList[attribute]}`);
          if (facetsToMap[attribute]) {
            const attributeValues = refinementList[attribute];
            if (attributeValues && attributeValues?.length == 1) {
              facetsToUrl[attribute] = attributeValues[0];
            }
          }
        }
      });

      if (sfacet?.length > 0 && typeof window !== 'undefined') {
        sessionStorage.setItem('pageLoadFacets', sfacet.join('|'));
        sessionStorage.setItem('isPageLoadFacetChecked', '1');
      }
    }
    let facetURl: any = '';
    if (Object.keys(facetsToUrl).length > 0 && (baseURl.includes('categories') || url.pathname.includes('brands'))) {
      const facetsArray: any = [];
      Object.keys(facetsToUrl).forEach((key) => {
        facetsArray[facetsToMap[key]] = slugifyFacet(facetsToUrl[key]);
      });
      facetURl = facetsArray.length ? facetsArray.filter((item: any) => item != '').join('/') : '';

      const normalizedFacetUrl = decodeURIComponent(facetURl);
      const normalizedPath = decodeURIComponent(url.pathname);

      if (!normalizedPath.includes(normalizedFacetUrl)) {
        url.pathname = url.pathname.endsWith('/') ? url.pathname + '_/' + facetURl : url.pathname + '/_/' + facetURl;
      }
    }

    let finalFacetString = facet.length > 0 ? facet.join('|') : undefined;

    if (!finalFacetString && isSortTransitionInProgress()) {
      const existingFacetParam = url.searchParams.get('facet');
      if (existingFacetParam) {
        finalFacetString = existingFacetParam;
      }
    }

    const routeState: CustomRouteState = {
      q: undefined,
      styleFinder: url.searchParams.get('styleFinder') ?? undefined,
    };

    // Preserve other query parameters
    url.searchParams.forEach((value, key) => {
      if (!['q', 'facet', 'sort', 'page', 'styleFinder', 'drawer', 'priceRange'].includes(key)) {
        routeState[key] = value;
      }
    });

    // Handle facet
    if (finalFacetString) {
      routeState.facet = finalFacetString;
      url.searchParams.set('facet', finalFacetString);
    } else {
      routeState.facet = undefined;
      url.searchParams.delete('facet');
    }

    // Handle sorting
    if (indexUiState.sortBy && indexName !== indexUiState.sortBy) {
      routeState.sort = indexUiState.sortBy;
    }

    // Handle pagination
    if (indexUiState.page && indexUiState.page >= 1) {
      routeState.page = indexUiState.page?.toString();
    } else {
      url.searchParams.delete('page');
    }

    // Handle query
    const existingQuery = url.searchParams.get('q');
    const currentQuery =
      indexUiState.query || (indexUiState.configure && (indexUiState.configure as any).query) || undefined;

    if (currentQuery !== undefined) {
      if (currentQuery !== existingQuery) {
        url.searchParams.set('q', currentQuery);
        url.searchParams.delete('page'); // Reset pagination on query change
      }
      routeState.q = currentQuery;
    } else if (existingQuery !== null) {
      routeState.q = existingQuery;
    }

    const orderedParams = new URLSearchParams();

    // Preserve the order of keys manually
    const paramOrder = ['q', 'facet', 'sort', 'page', 'styleFinder'];
    paramOrder.forEach((key) => {
      if (url.searchParams.has(key)) {
        orderedParams.set(key, url.searchParams.get(key) as string);
      }
    });

    // Add other query parameters not in paramOrder
    url.searchParams.forEach((value, key) => {
      if (!paramOrder.includes(key)) {
        orderedParams.set(key, value);
      }
    });

    // Reconstruct URL with ordered parameters
    const sortedQueryParams: string[] = [];
    orderedParams.forEach((value, key) => {
      sortedQueryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });
    // Reconstruct the sorted URL
    url.search = sortedQueryParams?.join('&')?.toString();

    // Replace state only if necessary
    if (
      url?.toString() !== window.location.href &&
      (baseURl.includes('categories') || url.pathname.includes('brands')) &&
      sessionStorage.getItem('isBackButtonCalled') === '0'
    ) {
      const refinedUrl = url?.toString()?.replace(/\+/g, '%20');
      window.history.pushState({ ...window.history.state, as: refinedUrl, url: refinedUrl }, '', refinedUrl);
    }
    return routeState;
  },

  routeToState(routeState) {
    const refinementList: Record<string, string[]> = {};
    const removeFacetOnReturn =
      typeof window !== 'undefined' ? sessionStorage.getItem('removeFacetOnReturn') === '1' : false;

    // Parse the facet string into refinementList
    if (routeState.facet && !removeFacetOnReturn) {
      const facets = routeState.facet.split('|');
      facets.forEach((facet) => {
        const [attribute, ...valueParts] = facet.split(':');
        const value = valueParts.join(':'); // Rejoin the remaining parts
        if (attribute && value) {
          refinementList[attribute] = value.split(',');
        }
      });
    }

    if (removeFacetOnReturn) {
      const url = new URL(window.location.href);
      url.searchParams.delete('facet');
      window.history.pushState({}, '', url?.toString());
      sessionStorage.removeItem('removeFacetOnReturn');
    }

    // Handle additional parameters
    const additionalParams: { [key: string]: any } = {};
    Object.keys(routeState).forEach((key) => {
      if (!['q', 'facet', 'sort', 'page', 'styleFinder'].includes(key)) {
        additionalParams[key] = routeState[key];
      }
    });

    return {
      [indexName]: {
        query: routeState.q,
        refinementList: Object.keys(refinementList).length > 0 ? refinementList : {},
        sortBy: routeState.sort,
        page: routeState.page ? parseInt(routeState.page) : 0,
        ...additionalParams,
      },
    };
  },
});

export default CustomStateMapping;
